import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {Text, TextSize} from '@Components/text';
import {LabelledCheckbox} from '@Components/labelled-checkbox';
import {CHECKBOX_TYPE} from '@Components/checkbox';
import {useEventWizardRegistrationDetails, useEventWizardTitle} from '@Components/event-wizard/event-wizard.hooks';
import {useDispatch} from 'react-redux';
import {onEventWizardRegistrationChange} from '@Components/event-wizard/event-wizard-slice';
import {toggleDisplayPreviewRegisterCTAs, toggleDisplayPreviewRegistrationForm, updatePreviewGuestInfoPhoneVisibility, updatePreviewRegistrationForm} from '@Components/event-wizard/preview/preview.helper';
import {EventWizardPremiumIcon} from '@Components/event-wizard/event-wizard-premium-icon';
import styles from './event-wizard-registrations.module.scss';
import {shouldUpsellUserForNumRegistrations, shouldUpsellUserForRegistrationOptions} from '@Components/event-wizard/event-wizard-registrations/event-wizard-registrations.types';
import {ClickableDiv} from '@Components/clickable-div';
import {EventUpsellType, openEventsUpsellDialog} from '@Libraries/events.library';
import {EventWizardRegistrationsButtonText} from '@Components/event-wizard/event-wizard-registrations-button-text';
import {EventWizardRegistrationsButtonLink} from '@Components/event-wizard/event-wizard-registrations-button-link';
import {ToggleSwitch, ToggleSwitchV2Size} from '@Components/toggle-switch-v2';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';

export function EventWizardRegistrations(): ReactElement<any> {
  const dispatch = useDispatch();
  const registrationDetails = useEventWizardRegistrationDetails();
  const title = useEventWizardTitle();

  const [areRegistrationsRequired, setAreRegistrationsRequired] = useState<boolean>(registrationDetails.areRegistrationsRequired);

  const toggleRegistrationsRequired = (): void => {
    const newAreRegistrationsRequired = !areRegistrationsRequired;
    const updatedFields = {
      ...registrationDetails,
      areRegistrationsRequired: newAreRegistrationsRequired,
    }
    dispatch(onEventWizardRegistrationChange(updatedFields));
    setAreRegistrationsRequired(newAreRegistrationsRequired);
    toggleDisplayPreviewRegistrationForm(newAreRegistrationsRequired);
    toggleDisplayPreviewRegisterCTAs(newAreRegistrationsRequired);
  };

  const getSignupFormFieldOptions = (): ReactElement<any> => {
    return (
      <div className="spacing-m-t-4">
        <div className={`${styles.optionContainer} flex-row-align-center spacing-m-b-3`}>
          <LabelledCheckbox label={window.i18next.t('pmwjs_email_required_asterick')} isChecked checkboxType={CHECKBOX_TYPE.LIGHTER} labelIcon="icon-envelope" />
        </div>

        <div className={`${styles.optionContainer} flex-row-align-center spacing-m-b-3`}>
          <LabelledCheckbox
            onChange={toggleName}
            label={window.i18next.t('pmwjs_name')}
            isChecked={shouldUpsellUserForRegistrationOptions() ? false : registrationDetails.name}
            checkboxType={CHECKBOX_TYPE.PRIMARY}
            labelIcon="icon-user"
          />
          {shouldUpsellUserForRegistrationOptions() ? <EventWizardPremiumIcon upsellType={EventUpsellType.REGISTRATION_OPTIONS} /> : undefined}
        </div>

        <div className={`${styles.optionContainer} flex-row-align-center`}>
          <LabelledCheckbox
            onChange={togglePhone}
            label={window.i18next.t('pmwjs_phone_number')}
            isChecked={shouldUpsellUserForRegistrationOptions() ? false : registrationDetails.phone}
            checkboxType={CHECKBOX_TYPE.PRIMARY}
            labelIcon="icon-phone"
          />
          {shouldUpsellUserForRegistrationOptions() ? <EventWizardPremiumIcon upsellType={EventUpsellType.REGISTRATION_OPTIONS} /> : undefined}
        </div>
      </div>
    );
  };

  const toggleName = (isChecked: boolean): void => {
    if (shouldUpsellUserForRegistrationOptions()) {
        openEventsUpsellDialog(EventUpsellType.REGISTRATION_OPTIONS, undefined);
      return;
    }
    const updatedFields = {
      ...registrationDetails,
      name: isChecked,
    };
    dispatch(onEventWizardRegistrationChange(updatedFields));
    updatePreviewRegistrationForm(updatedFields, title);
  };

  const togglePhone = (isChecked: boolean): void => {
    if (shouldUpsellUserForRegistrationOptions()) {
        openEventsUpsellDialog(EventUpsellType.REGISTRATION_OPTIONS, undefined);
      return;
    }
    const updatedFields = {
      ...registrationDetails,
      phone: isChecked,
    };
    dispatch(onEventWizardRegistrationChange(updatedFields));
    updatePreviewRegistrationForm(updatedFields, title);
    updatePreviewGuestInfoPhoneVisibility(isChecked);
  };

  const getNumGuestsLimitContent = (): ReactElement<any> => {
    if (!shouldUpsellUserForNumRegistrations()) {
      return <></>;
    }
    return (
      <div className="flex-row-align-center spacing-p-t-2 spacing-p-b-2 spacing-p-l-3 spacing-p-r-3 bg-premium-10 justify-content-between radius-8 spacing-m-b-4">
        <div className={styles.numGuestsTextContainer}>
          <Text className="flex-row-align-center" val={window.i18next.t('pmwjs_collect_maximum_100_registrations_free_plan')} size={TextSize.XXSMALL} />
        </div>
        <ClickableDiv
          onClick={() => {
              openEventsUpsellDialog(EventUpsellType.NUM_GUESTS_LIMIT, undefined);
          }}
          className="flex-row-align-center _cursor-pointer"
        >
          <EventWizardPremiumIcon upsellType={EventUpsellType.NUM_GUESTS_LIMIT} />
          <Text className="spacing-m-l-1 content-premium" val={window.i18next.t('pmwjs_upgrade_to_unlimited')} size={TextSize.XXSMALL} bold underline />
        </ClickableDiv>
      </div>
    );
  };

  const getRegistrationFieldsContent = (): ReactElement => {
    return (
      <div>
        <div className="flex-row-align-center justify-content-start">
          <Icon icon="icon-list" size={IconSize.SIZE_ICON_16} className="spacing-m-r-1" />
          <Text className="flex-row-align-center" val={window.i18next.t('pmwjs_details_shared_by_guest')} size={TextSize.XXSMALL} bold />
        </div>
        <div className="spacing-p-l-4">{getSignupFormFieldOptions()}</div>
      </div>
    );
  };

  const getRegistrationButtonContent = (): ReactElement => {
    return (
      <>
        <div className="spacing-m-b-4">
          <EventWizardRegistrationsButtonText />
        </div>
        <div className="spacing-m-b-4">
          <EventWizardRegistrationsButtonLink />
        </div>
      </>
    );
  };

  const getRegistrationsRequiredToggle = (): ReactElement => {
    return (
      <div className="flex-row-align-center justify-content-between bg-neutral-3 spacing-p-t-2 spacing-p-b-2 spacing-p-r-3 spacing-p-l-3 radius-8">
        <Text val={window.i18next.t('pmwjs_activate_button')} size={TextSize.XXSMALL} />
        <ToggleSwitch isChecked={areRegistrationsRequired} onChange={toggleRegistrationsRequired} size={ToggleSwitchV2Size.MEDIUM} />
      </div>
    );
  };

  return (
    <div>
      {getRegistrationsRequiredToggle()}
      <div
        className={`${styles.registrationSection} ${!areRegistrationsRequired ? '_hidden' : ''}`}
      >
        <div className="spacing-m-t-4"></div>
        {getRegistrationButtonContent()}
        {getNumGuestsLimitContent()}
        {getRegistrationFieldsContent()}
      </div>
    </div>
  );
}
